import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Star, MapPin, Clock, ChevronRight, Video, Users, 
  GraduationCap, BookOpen, AlertTriangle, Search
} from 'lucide-react';
import { TutorTrustBadges } from './EducationBadges';
import ExamCalendarSection from './ExamCalendarSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== TUTOR CARD VARIANTS ====================

// Standard Tuition Card
function TuitionCard({ tutor, currencySymbol, onBook }) {
  return (
    <Card 
      className="group hover:shadow-lg transition-all duration-300 overflow-hidden border-l-4 border-l-indigo-500"
      data-testid={`tutor-card-${tutor.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <img 
            src={tutor.profile_image} 
            alt={tutor.name}
            className="w-12 h-12 rounded-full object-cover ring-2 ring-indigo-100"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-indigo-200 text-indigo-600">
                📘 Verified Tutor
              </Badge>
            </div>
            <h3 className="font-semibold text-gray-900 truncate">{tutor.name}</h3>
            <p className="text-xs text-gray-500 truncate">{tutor.subjects?.join(', ')}</p>
          </div>
        </div>

        {/* Delivery & Level */}
        <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
          {tutor.availability?.online && (
            <span className="flex items-center gap-1">
              <Video className="w-3 h-3" /> Online
            </span>
          )}
          {tutor.availability?.in_person && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> In-Person
            </span>
          )}
          <span className="text-gray-300">•</span>
          <span>{tutor.levels?.join(', ')}</span>
        </div>

        {/* Features */}
        <div className="space-y-1 mb-3">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="text-green-500">✔</span> Personalised lessons
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="text-green-500">✔</span> Flexible schedule
          </div>
        </div>

        {/* Badges */}
        <div className="mb-3">
          <TutorTrustBadges badges={tutor.badges} maxDisplay={3} size="xs" />
        </div>

        {/* Rating & Price */}
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold text-sm">{tutor.rating}</span>
            <span className="text-xs text-gray-400">({tutor.total_reviews})</span>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">From</p>
            <p className="font-bold text-indigo-600">{currencySymbol}{tutor.hourly_rate}/hr</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <Button 
            size="sm" 
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            onClick={() => onBook && onBook(tutor)}
          >
            Book Trial
          </Button>
          <Button size="sm" variant="outline" className="flex-1">
            Message
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Exam Prep Card
function ExamPrepCard({ tutor, currencySymbol, onBook }) {
  return (
    <Card 
      className="group hover:shadow-lg transition-all duration-300 overflow-hidden border-l-4 border-l-amber-500"
      data-testid={`exam-prep-card-${tutor.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <img 
            src={tutor.profile_image} 
            alt={tutor.name}
            className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-100"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-200 text-amber-600">
                📝 Exam Prep
              </Badge>
            </div>
            <h3 className="font-semibold text-gray-900 truncate">{tutor.name}</h3>
            <p className="text-xs text-gray-500 truncate">{tutor.subjects?.join(' • ')}</p>
          </div>
        </div>

        {/* Exam focus */}
        <div className="flex items-center gap-2 mb-3">
          <Badge className="bg-amber-100 text-amber-700 text-xs">
            🎯 {tutor.levels?.join(', ')} Preparation
          </Badge>
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg mb-3 text-xs text-amber-700">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>No grade guarantees</span>
        </div>

        {/* Badges */}
        <div className="mb-3">
          <TutorTrustBadges badges={tutor.badges} maxDisplay={3} size="xs" />
        </div>

        {/* Rating & Price */}
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold text-sm">{tutor.rating}</span>
            <span className="text-xs text-gray-400">({tutor.total_reviews})</span>
          </div>
          <p className="font-bold text-amber-600">{currencySymbol}{tutor.hourly_rate}/session</p>
        </div>

        {/* Action */}
        <Button 
          size="sm" 
          className="w-full mt-3 bg-amber-600 hover:bg-amber-700"
          onClick={() => onBook && onBook(tutor)}
        >
          Start Prep
        </Button>
      </CardContent>
    </Card>
  );
}

// Online Course Card
function OnlineCourseCard({ course, currencySymbol, onEnrol }) {
  return (
    <Card 
      className="group hover:shadow-lg transition-all duration-300 overflow-hidden border-l-4 border-l-emerald-500"
      data-testid={`course-card-${course.id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-emerald-200 text-emerald-600 mb-2">
          💻 Online Course
        </Badge>

        <h3 className="font-semibold text-gray-900 mb-1">{course.name || course.subjects?.[0]}</h3>
        <p className="text-xs text-gray-500 mb-3">Self-paced • Certificate of completion</p>

        {/* Info notice */}
        <div className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg mb-3 text-xs text-blue-700">
          <span>ℹ️</span>
          <span>Non-accredited course</span>
        </div>

        {/* Badges */}
        <div className="mb-3">
          <TutorTrustBadges badges={course.badges} maxDisplay={2} size="xs" />
        </div>

        {/* Price & Action */}
        <div className="flex items-center justify-between pt-3 border-t">
          <p className="font-bold text-emerald-600">{currencySymbol}{course.hourly_rate || 49} one-time</p>
          <Button 
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onEnrol && onEnrol(course)}
          >
            Enrol Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== SUBCATEGORY GRID ====================

function SubcategoryCard({ subcategory, onClick }) {
  return (
    <Card 
      className="group hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer"
      onClick={() => onClick && onClick(subcategory)}
      data-testid={`subcat-${subcategory.slug}`}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-xl group-hover:scale-110 transition-transform">
          {subcategory.icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-gray-900 truncate">{subcategory.name}</h3>
          <p className="text-xs text-gray-500">{subcategory.service_count} services</p>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-indigo-600 transition-colors" />
      </CardContent>
    </Card>
  );
}

// ==================== MAIN COMPONENT ====================

export default function EducationTuitionServicesDisplay({ 
  country = 'UK',
  subcategorySlug = null,
  onNavigate,
}) {
  const navigate = useNavigate();
  const [subcategories, setSubcategories] = useState([]);
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryInfo, setCategoryInfo] = useState(null);

  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch subcategories
        const subcatRes = await fetch(
          `${API_URL}/api/education-tuition/config/subcategories?country=${country}`
        );
        const subcatData = await subcatRes.json();
        
        if (subcatData.success) {
          setSubcategories(subcatData.subcategories);
          setCategoryInfo({
            currency: subcatData.currency,
            currency_symbol: subcatData.currency_symbol,
            country_name: subcatData.country_name,
          });
        }

        // Fetch tutors
        const tutorsRes = await fetch(
          `${API_URL}/api/education-tuition/tutors?country=${country}&limit=6`
        );
        const tutorsData = await tutorsRes.json();
        
        if (tutorsData.success) {
          setTutors(tutorsData.tutors);
        }
      } catch (error) {
        console.error('Failed to fetch education data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [country]);

  const handleSubcategoryClick = (subcat) => {
    if (onNavigate) {
      onNavigate(`/category/education-tuition/${subcat.slug}`);
    } else {
      navigate(`/category/education-tuition/${subcat.slug}`);
    }
  };

  const handleBookTrial = (tutor) => {
    navigate(`/book/education/${tutor.id}?trial=true`);
  };

  const handleExamFilter = (subcategory, examType) => {
    navigate(`/category/education-tuition/${subcategory}?exam_type=${examType}`);
  };

  if (loading) {
    return (
      <div className="py-4" data-testid="education-loading">
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-gray-100 rounded-lg" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Separate tutors by card type
  const examPrepTutors = tutors.filter(t => 
    t.subcategories?.some(s => s.includes('exam') || s.includes('national'))
  );
  const regularTutors = tutors.filter(t => 
    !t.subcategories?.some(s => s.includes('exam') || s.includes('national'))
  );
  const courseTutors = tutors.filter(t => 
    t.subcategories?.some(s => s.includes('course') || s.includes('it-coding'))
  );

  return (
    <div className="py-4 space-y-8" data-testid="education-services-display">
      {/* Exam Calendar Section */}
      <ExamCalendarSection 
        country={country}
        onFilterByExam={handleExamFilter}
        className="mb-8"
      />

      {/* Subcategories Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Browse by Category</h2>
            <p className="text-sm text-gray-500">Find the right educational service</p>
          </div>
          <Button variant="ghost" size="sm" className="text-indigo-600">
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {subcategories.slice(0, 8).map((subcat) => (
            <SubcategoryCard 
              key={subcat.slug}
              subcategory={subcat}
              onClick={handleSubcategoryClick}
            />
          ))}
        </div>
        
        {/* Country-specific subcategories highlight */}
        {subcategories.filter(s => s.country_only).length > 0 && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            {subcategories.filter(s => s.country_only).map((subcat) => (
              <Card 
                key={subcat.slug}
                className="group hover:shadow-md cursor-pointer bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200"
                onClick={() => handleSubcategoryClick(subcat)}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-xl shadow-sm">
                    {subcat.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-indigo-900 truncate">{subcat.name}</h3>
                    <p className="text-xs text-indigo-600">{country} Specific</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Featured Tutors */}
      {tutors.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Featured Tutors</h2>
              <p className="text-sm text-gray-500">Top-rated educators in your area</p>
            </div>
            <Button variant="ghost" size="sm" className="text-indigo-600">
              View All Tutors <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {regularTutors.slice(0, 3).map((tutor) => (
              <TuitionCard 
                key={tutor.id}
                tutor={tutor}
                currencySymbol={currencySymbol}
                onBook={handleBookTrial}
              />
            ))}
          </div>
        </div>
      )}

      {/* Exam Prep Section */}
      {examPrepTutors.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Exam Preparation</h2>
              <p className="text-sm text-gray-500">Get ready for important exams</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {examPrepTutors.slice(0, 3).map((tutor) => (
              <ExamPrepCard 
                key={tutor.id}
                tutor={tutor}
                currencySymbol={currencySymbol}
                onBook={handleBookTrial}
              />
            ))}
          </div>
        </div>
      )}

      {/* Online Courses Section */}
      {courseTutors.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Online Courses</h2>
              <p className="text-sm text-gray-500">Learn at your own pace</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4">
            {courseTutors.slice(0, 3).map((course) => (
              <OnlineCourseCard 
                key={course.id}
                course={course}
                currencySymbol={currencySymbol}
                onEnrol={handleBookTrial}
              />
            ))}
          </div>
        </div>
      )}

      {/* Search CTA */}
      <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold mb-1">Can't find what you're looking for?</h3>
            <p className="text-indigo-100 text-sm">Search our full database of tutors and courses</p>
          </div>
          <Button variant="secondary" className="bg-white text-indigo-600 hover:bg-indigo-50">
            <Search className="w-4 h-4 mr-2" />
            Search All
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
